import * as React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useLocation, useResolvedPath } from 'react-router-dom'
import type { Location } from 'react-router-dom'
import { useOptimisticSearchParams } from 'nuqs/adapters/react-router/v6'

import { usePostComposer } from '~publish/hooks/usePostComposer'
import {
  parseQueryParams,
  serializeQueryParams,
} from '~publish/hooks/useQueryParam'
import { postDetailsRoute } from '~publish/legacy/routes'

/**
 * Query params the post-details dialog injects into the URL when it opens (see
 * {@link usePostDetailsDialog}'s `constructDetailsDialogLocation`). They are
 * dialog-scoped and must be removed when navigating back so they don't leak
 * onto the parent view.
 */
const DIALOG_ONLY_PARAMS = ['cta', 'expandText', 'notesFocus']

/**
 * Build the search string to return to when the post-details dialog closes.
 *
 * Preserves the parent view's full query state (Insights date range,
 * comparison, post filters, …) and strips only the params the dialog itself
 * injected on open. This is the inverse of `constructDetailsDialogLocation`.
 */
export function getReturnSearchOnDialogClose(search: string): string {
  const params = { ...parseQueryParams(search) }
  for (const key of DIALOG_ONLY_PARAMS) {
    delete params[key]
  }
  const serialized = serializeQueryParams(params)
  return serialized ? `?${serialized}` : ''
}

export function useOnPostComposerClose(callback: () => void): void {
  const { isOpen } = usePostComposer()
  const [previousIsOpen, setPreviousIsOpen] = useState(isOpen)
  useEffect(() => {
    setPreviousIsOpen(isOpen)
  }, [isOpen])

  useEffect(() => {
    setTimeout(() => {
      if (!isOpen && previousIsOpen !== isOpen) {
        callback()
      }
    }, 1000)
  }, [isOpen, previousIsOpen, callback])
}

type OpenDetailsDialogArgs = {
  cta: CTA
  expandText?: 'true' | 'false'
  notesFocus?: 'true' | 'false'
}

export const usePostDetailsDialog = ({
  postId,
  useSingularPath = false,
}: {
  postId: string
  /**
   * Use `/post/:postId` instead of `/posts/:postId`.
   * This is needed for the community list view where `/posts` conflicts with the posts view route.
   */
  useSingularPath?: boolean
}): {
  constructDetailsDialogLocation: (args: OpenDetailsDialogArgs) => Location
  openDetailsDialog: (args: OpenDetailsDialogArgs) => void
} => {
  const resolvedPath = useResolvedPath('.')
  const navigate = useNavigate()
  const location = useLocation()
  // nuqs params (the Calendar's `date`) never reach react-router, so
  // `location.search` can be missing them. Read the browser URL instead.
  const searchParams = useOptimisticSearchParams()

  const postPath = useSingularPath
    ? `/post/${postId}`
    : postDetailsRoute.getRoute({ postId })
  const pathname = `${resolvedPath.pathname}${postPath}`

  const constructDetailsDialogLocation = React.useCallback(
    ({ cta, expandText = 'false', notesFocus }: OpenDetailsDialogArgs) => {
      const currentParams = parseQueryParams(searchParams.toString())
      const newSearchParams = serializeQueryParams({
        ...currentParams,
        cta,
        expandText,
        ...(notesFocus ? { notesFocus } : {}),
      })
      return {
        ...location,
        pathname,
        search: newSearchParams,
      }
    },
    [pathname, location, searchParams],
  )

  const openDetailsDialog = React.useCallback(
    ({ cta, expandText = 'false', notesFocus }: OpenDetailsDialogArgs) => {
      navigate(constructDetailsDialogLocation({ cta, expandText, notesFocus }))
    },
    [navigate, constructDetailsDialogLocation],
  )

  return {
    constructDetailsDialogLocation,
    openDetailsDialog,
  }
}
